// Klinik şablon sayfalarının (Klinik Şablonları menüsü) ortak düz liste iskeleti.
// docs/design-principles.md §3: beyaz zemin, kutusuz — başlık şeridi (PageHeaderBar,
// sağda "Yeni Şablon") → araç satırı (arama) → durum çipleri → çerçevesiz tablo.
// Satırın tamamı tıklanır (düzenleme drawer'ı açılır); "İşlem" sütunu yok.
// Liste sunucuda süzülür ve sayfalanır (query, status, page, limit).

import React from "react";
import { Alert, Box, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { useClinicTheme } from "../../theme";
import { COLORS } from "../../styles/uiConstants";
import { ActionButton, ChoiceChipGroup, SearchField } from "../atoms";
import { PageHeaderBar } from "../molecules";
import DataTableCard from "./DataTableCard";
import {
  ClickableRow,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PlainHead,
  ROWS_PER_PAGE_OPTIONS,
  SkeletonRows,
  cellSx,
} from "./ClinicRecordList/recordTableParts";

export const TEMPLATE_STATUS_OPTIONS = [
  { value: "all", label: "Tümü" },
  { value: "active", label: "Aktif" },
  { value: "inactive", label: "Pasif" },
];

/** Sunucu `status` parametresi: "all" gönderilmez. */
export const statusParam = (status) => (status && status !== "all" ? status : undefined);

/**
 * @param {{key: string, label: string, width: string}[]} columns - yüzde genişlikler (toplam 100%)
 * @param {(row) => React.ReactNode[]} renderRowCells - sütun sırasıyla hücre İÇERİKLERİ
 *   (TableCell'i organism çizer; ilk/son hücre başlık hizasında)
 * @param {(row) => void} onRowClick - satır tıklanınca (düzenleme drawer'ı)
 * @param {string} [emptyTitle] / emptyMessage - hiç şablon yokken başlık ve açıklama
 * @param {string} [status] / onStatusChange - verilirse Tümü/Aktif/Pasif çipleri görünür
 */
export default function TemplateListPage({
  icon,
  title,
  subtitle,
  query,
  onQueryChange,
  searchPlaceholder = "Şablon adı ile ara",
  createLabel = "Yeni Şablon",
  onCreate,
  status,
  onStatusChange,

  errorMsg,
  isLoading = false,

  columns = [],
  rows = [],
  renderRowCells,
  onRowClick,
  getRowKey,

  emptyIcon,
  emptyTitle,
  emptyMessage,

  page = 0,
  rowsPerPage = 25,
  total = 0,
  onPageChange,
  onRowsPerPageChange,

  children,
}) {
  const { palette } = useClinicTheme();
  const cols = columns.length;
  const q = String(query || "").trim();

  const createLink = (label) =>
    onCreate ? (
      <Box
        component="button"
        type="button"
        onClick={onCreate}
        sx={{
          font: "inherit",
          p: 0,
          border: 0,
          bgcolor: "transparent",
          cursor: "pointer",
          color: palette.primary,
          fontWeight: 600,
          textDecoration: "underline",
          textUnderlineOffset: 3,
        }}
      >
        {label}
      </Box>
    ) : null;

  const empty = q
    ? {
        title: "Şablon bulunamadı",
        message: (
          <>
            “{q}” aramanızla eşleşen şablon yok. Aramayı değiştirin
            {onCreate ? <> veya {createLink("yeni şablon oluşturun")}</> : null}.
          </>
        ),
      }
    : status === "active"
    ? { title: "Aktif şablon yok", message: "Tüm şablonlar pasif durumda." }
    : status === "inactive"
    ? { title: "Pasif şablon yok", message: "Pasife alınan şablonlar burada listelenir." }
    : {
        title: emptyTitle || "Henüz şablon yok",
        message: onCreate ? (
          <>
            {emptyMessage || "Sık kullandığınız seçimleri şablon olarak kaydedin."} İlk şablonu{" "}
            {createLink("oluşturun")}.
          </>
        ) : (
          emptyMessage || "Henüz şablon oluşturulmamış."
        ),
      };

  return (
    <>
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: COLORS.white, minHeight: 0 }}>
        <PageHeaderBar
          icon={icon}
          title={title}
          subtitle={subtitle}
          action={
            onCreate ? (
              <ActionButton variant="add" palette={palette} size="medium" label={createLabel} onClick={onCreate} />
            ) : null
          }
        />

        {/* Araç satırı */}
        <Box
          sx={{
            px: { xs: 2, md: 4 },
            pt: 2.5,
            pb: onStatusChange ? 0 : 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            flexShrink: 0,
          }}
        >
          <SearchField
            placeholder={searchPlaceholder}
            value={query || ""}
            onChange={(v) => onQueryChange?.(v)}
            autoComplete="off"
            sx={{ width: { xs: "100%", sm: 340 } }}
          />
        </Box>

        {/* Durum çipleri */}
        {onStatusChange ? (
          <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, pb: 2, flexShrink: 0 }}>
            <ChoiceChipGroup
              ariaLabel="Şablon durumu"
              options={TEMPLATE_STATUS_OPTIONS.map((o) =>
                o.value === (status || "all") && !isLoading ? { ...o, label: `${o.label} · ${total}` } : o
              )}
              value={status || "all"}
              onChange={onStatusChange}
            />
          </Box>
        ) : null}

        {errorMsg ? (
          <Alert severity="error" sx={{ mx: { xs: 2, md: 4 }, mb: 1.5, borderRadius: 2, flexShrink: 0 }}>
            {errorMsg}
          </Alert>
        ) : null}

        {/* Tablo — kalan alanı doldurur, kendi içinde kayar */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
          <DataTableCard
      loading={Boolean(isLoading)}
            variant="flat"
            empty={!isLoading && rows.length === 0}
            emptyIcon={emptyIcon}
            emptyTitle={empty.title}
            emptyMessage={empty.message}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={total}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          >
            <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  {columns.map((c, i) => (
                    <PlainHead
                      key={c.key || c.label}
                      sx={{
                        width: c.width,
                        ...(i === 0 ? EDGE_L : {}),
                        ...(i === cols - 1 ? EDGE_R : {}),
                      }}
                    >
                      {c.label}
                    </PlainHead>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <SkeletonRows rows={Math.min(10, rowsPerPage)} cols={cols} />
                ) : rows.length ? (
                  rows.map((row, idx) => {
                    const cells = renderRowCells ? renderRowCells(row, idx) : [];
                    return (
                      <ClickableRow
                        key={getRowKey ? getRowKey(row, idx) : row?._id ?? idx}
                        onClick={() => onRowClick?.(row)}
                        palette={palette}
                      >
                        {cells.map((content, i) => (
                          <TableCell
                            key={columns[i]?.key || i}
                            sx={{ ...cellSx, ...(i === 0 ? EDGE_L : {}), ...(i === cols - 1 ? EDGE_R : {}) }}
                          >
                            {content}
                          </TableCell>
                        ))}
                      </ClickableRow>
                    );
                  })
                ) : (
                  <FillerRow cols={cols} />
                )}
              </TableBody>
            </Table>
          </DataTableCard>
        </Box>
      </Box>
      {children}
    </>
  );
}
